import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useContentToken } from '@vibesites/runtime/react';

/**
 * Decorative between-section divider — a thin tapered line with a centered
 * accent (diamond, dots, or short bar). Visual-only element meant to separate
 * same-background content sections. Takes props to tune its look per page
 * entry, with an optional small eyebrow label operators can fill in.
 *
 * Shared-component pattern: supports `tokenPrefix` so multiple instances on
 * the same page can carry their own label without colliding. The visible
 * wrapper uses the dynamic path; a hidden schema anchor carries the canonical
 * literal path for the schema linter.
 */

interface SectionDividerProps {
  /** Accent shape at the center of the line. */
  variant?: 'diamond' | 'dots' | 'bar';
  /** Background color — match the surrounding section so the divider blends. */
  background?: 'background' | 'surface' | 'transparent';
  /** Vertical rhythm around the line. */
  spacing?: 'tight' | 'normal' | 'roomy';
  /** Per-instance token scope. Default is the canonical slot name. */
  tokenPrefix?: string;
}

export function SectionDivider({
  variant = 'diamond',
  background = 'background',
  spacing = 'normal',
  tokenPrefix,
}: SectionDividerProps) {
  const prefix = tokenPrefix || 'sectiondivider';
  const labelPath = `${prefix}.label`;
  const label = useContentToken(labelPath, { defaultValue: '' });

  const bgClass =
    background === 'surface'
      ? 'bg-[var(--vibe-color-surface)]'
      : background === 'transparent'
        ? 'bg-transparent'
        : 'bg-[var(--vibe-color-background)]';

  const padClass =
    spacing === 'tight'
      ? 'py-4 md:py-6'
      : spacing === 'roomy'
        ? 'py-10 md:py-14'
        : 'py-6 md:py-10';

  return (
    <div className={`w-full ${bgClass} ${padClass}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {label ? (
          <EditableToken
            path={labelPath}
            as="p"
            className="text-center font-body text-[11px] uppercase tracking-[0.35em] text-[var(--vibe-color-text-muted)] mb-4"
          >
            {label}
          </EditableToken>
        ) : null}

        <div className="flex items-center justify-center">
          {variant === 'diamond' ? (
            <>
              <span
                aria-hidden
                className="block w-10 sm:w-14 h-0.5 rounded-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]"
              />
              <span
                aria-hidden
                className="block w-2.5 h-2.5 rotate-45 mx-3 bg-[var(--vibe-color-primary)]"
              />
              <span
                aria-hidden
                className="block w-10 sm:w-14 h-0.5 rounded-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]"
              />
            </>
          ) : variant === 'dots' ? (
            <span aria-hidden className="flex items-center gap-2">
              <span className="block w-1.5 h-1.5 rounded-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]" />
              <span className="block w-2.5 h-2.5 rounded-full bg-[var(--vibe-color-primary)]" />
              <span className="block w-1.5 h-1.5 rounded-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]" />
            </span>
          ) : (
            <span
              aria-hidden
              className="block w-16 sm:w-20 h-1 rounded-full bg-[var(--vibe-color-primary)]"
            />
          )}
        </div>
      </div>

      {/* Schema-lint anchor — hidden, never interactive. Carries the canonical
          literal path for static detection when this shared component is
          mounted with a tokenPrefix. */}
      <EditableToken
        path="sectiondivider.label"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SectionDivider',
  displayName: 'Section Divider',
  tokens: {
    'sectiondivider.label': {
      label: 'Optional Label',
      type: 'text',
      defaultValue: '',
      description: 'Optional small eyebrow text displayed above the divider line.',
    },
  },
};

(SectionDivider as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SectionDivider;
